import { autoUpdate, flip, inline, offset, shift, size, useFloating } from '@floating-ui/react-dom'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { PressResponder, useKeyboard } from '@react-aria/interactions'
import { useOverlayTrigger } from '@react-aria/overlays'
import { mergeProps, useObjectRef } from '@react-aria/utils'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { Popover } from 'components/Popover/Popover'
import { cloneElement, forwardRef, ReactElement, ReactNode, useEffect } from 'react'
import { useKey } from 'react-use'
import { DropdownTriggerProps } from './Types'

const DropdownTrigger = forwardRef<HTMLElement, DropdownTriggerProps>(
  (
    {
      children,
      placement,
      onClose,
      popoverClassName,
      onOpenChange,
      noPressed,
      containFocus = true,
      autoFocus = true,
      noOverlay,
      useSqueeze = true,
      maxHeight,
      autoUpdateOptions = {},
      forceOpen = false,
      offsetOptions = 4,
      shiftOptions = { padding: 4 },
      shouldCloseOnBlur = false,
      disableCloseOnInteractOutside = false,
      restoreFocus = true,
      preventFocusOnPress = false,
      __useNonAriaPressResponder = false,
      __UNSAFE_popoverStyle = {},
      root,
      ...props
    },
    fRef,
  ) => {
    let [trigger, content] = children
    if (!content) {
      ;[, content] = children as [ReactElement, (close: () => void) => ReactNode]
    }

    const { keyboardProps } = useKeyboard(props)
    const ref = useObjectRef<HTMLElement>(fRef)

    const {
      x,
      y,
      refs,
      placement: computedPlacement,
    } = useFloating({
      placement: placement,
      strategy: 'fixed',
      middleware: [
        offset(offsetOptions),
        inline(),
        flip(),
        shift(shiftOptions),
        size(
          useSqueeze
            ? {
                padding: 16,
                apply({ availableHeight, elements }) {
                  Object.assign(elements.floating.style, {
                    maxHeight: `${Math.max(96, maxHeight ?? availableHeight)}px`,
                  })
                },
              }
            : {},
        ),
      ],
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
          ...autoUpdateOptions,
        })
      },
    })
    const overlayState = useOverlayTriggerState({
      onOpenChange: (isOpen) => {
        refs.setFloating(null)
        onOpenChange?.(isOpen)
      },
    })
    const { triggerProps, overlayProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, ref)

    useEffect(() => {
      if (forceOpen) overlayState.open()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceOpen])

    useKey('Escape', () => overlayState.close(), { options: true })

    let pressResponder = (
      <PressResponder
        {...triggerProps}
        ref={ref}
        isPressed={overlayState.isOpen && !noPressed}
        onPress={overlayState.open}
        preventFocusOnPress={preventFocusOnPress}
      >
        {cloneElement(
          trigger as ReactElement,
          mergeProps((trigger as ReactElement).props, keyboardProps, {
            ref: (r: HTMLElement) => {
              ref.current = r
              refs.setReference(r)
            },
          }),
        )}
      </PressResponder>
    )

    if (__useNonAriaPressResponder) {
      pressResponder = cloneElement(
        trigger as ReactElement,
        mergeProps((trigger as ReactElement).props, keyboardProps, {
          onClick: () => overlayState.open(),
          ref: (r: HTMLElement) => {
            ref.current = r
            refs.setReference(r)
          },
        }),
      )
    }

    return (
      <>
        {pressResponder}
        {overlayState.isOpen && (
          <FloatingPortal id="floating-ui-root" root={root}>
            <Popover
              restoreFocus={restoreFocus}
              style={{
                position: 'fixed',
                top: y || 0,
                left: x || 0,
                visibility: x === null || y === null ? 'hidden' : 'visible',
                ...__UNSAFE_popoverStyle,
              }}
              {...overlayProps}
              className={popoverClassName}
              isOpen={overlayState.isOpen}
              shouldCloseOnInteractOutside={(element) => {
                if (disableCloseOnInteractOutside) {
                  return false
                }
                if (!element) {
                  return false
                }
                return !element.closest('.ignore-click-outside')
              }}
              onClose={() => {
                onClose?.()
                overlayState.close()
              }}
              ref={refs.setFloating}
              isDismissable
              shouldCloseOnBlur={shouldCloseOnBlur}
              containFocus={containFocus}
              autoFocus={autoFocus}
              noOverlay={noOverlay}
            >
              {typeof content === 'function'
                ? content(overlayState.close, overlayState.isOpen, computedPlacement !== placement)
                : content}
            </Popover>
          </FloatingPortal>
        )}
      </>
    )
  },
)

export { DropdownTrigger }
